import { useRef, useCallback } from 'react';

/**
 * Custom hook to preserve scroll position during component state changes
 * This is useful for preventing unwanted scrolling when editing forms or toggling UI states
 */
export const useScrollPreservation = () => {
  const scrollPositionRef = useRef<number>(0);

  /**
   * Preserve the current scroll position
   */
  const preserveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
  }, []);

  /**
   * Restore the previously preserved scroll position
   */
  const restoreScrollPosition = useCallback(() => {
    if (scrollPositionRef.current > 0) {
      // Use smooth scrolling for better user experience
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'auto' // Use 'auto' to prevent smooth scrolling that might interfere
      });
      
      // Double-check scroll position after a brief delay to ensure it was restored
      setTimeout(() => {
        if (Math.abs(window.scrollY - scrollPositionRef.current) > 10) {
          window.scrollTo(0, scrollPositionRef.current);
        }
      }, 50);
    }
  }, []);

  /**
   * Execute a function while preserving scroll position
   * @param fn - Function to execute
   * @param delay - Delay before restoring scroll position (default: 0)
   */
  const executeWithScrollPreservation = useCallback(async (
    fn: () => Promise<void> | void,
    delay: number = 0
  ) => {
    preserveScrollPosition();
    
    try {
      await fn();
    } finally {
      // Restore scroll position after the specified delay
      // Use requestAnimationFrame for better timing with DOM updates
      if (delay === 0) {
        requestAnimationFrame(restoreScrollPosition);
      } else {
        setTimeout(restoreScrollPosition, delay);
      }
    }
  }, [preserveScrollPosition, restoreScrollPosition]);

  return {
    preserveScrollPosition,
    restoreScrollPosition,
    executeWithScrollPreservation
  };
};
